import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, ArrowUp, FileText, Loader2, MessagesSquare, Paperclip, Wrench, X } from "lucide-react";
import { Orb } from "@yogesharc/thinking-orbs";
import Layout from "@/components/Layout";
import { getApiBase } from "@/lib/httpClient";
import { Button } from "@/components/ui/button";
import Markdown from "@/components/Markdown";
import { LEITURA } from "../lib/larguras";

// Rótulo do que o assistente consultou. Mostrar isso é o que separa uma
// resposta verificável de um palpite: o advogado vê de onde saiu o dado.
const ROTULO_FERRAMENTA = {
  buscar_processos: "consultou seus processos",
  detalhar_processo: "abriu o processo",
  buscar_intimacoes: "consultou suas intimações",
  listar_prazos: "consultou sua agenda",
  buscar_clientes: "consultou seus clientes",
};

export function rotuloDasFerramentas(ferramentas) {
  const unicas = [...new Set(ferramentas || [])];
  if (!unicas.length) return "";
  return unicas.map((f) => ROTULO_FERRAMENTA[f] || f).join(" · ");
}

// O que o MarkItDown lê. Fora disto, o usuário é avisado antes de enviar.
export const FORMATOS_ACEITOS = ".pdf,.docx,.doc,.xlsx,.xls,.pptx,.csv,.txt,.md,.html,.htm,.json,.xml,.rtf";
// Teto por plano, informado pelo servidor. O valor local é só o padrão até a
// resposta chegar — quem decide é o backend.
export const LIMITE_MB_PADRAO = 2;

export function formatarTamanho(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const SUGESTOES = [
  "O que chegou de intimação esta semana?",
  "Quais processos meus estão parados?",
  "O que vence nos próximos 15 dias?",
  "Resuma o processo 0000000-00.0000.0.00.0000",
];

export default function ChatPage() {
  const [conexao, setConexao] = useState(undefined); // undefined = carregando
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [conversaId, setConversaId] = useState(null);
  const [anexo, setAnexo] = useState(null);
  const [anexando, setAnexando] = useState(false);
  const [erroAnexo, setErroAnexo] = useState("");
  const [limiteMb, setLimiteMb] = useState(LIMITE_MB_PADRAO);
  const fim = useRef(null);
  const campoArquivo = useRef(null);

  useEffect(() => {
    axios
      .get(`${getApiBase()}/chat/provedores`, { withCredentials: true })
      .then((r) => {
        setConexao(r.data?.conexao || null);
        if (r.data?.anexo_limite_mb) setLimiteMb(r.data.anexo_limite_mb);
      })
      .catch(() => setConexao(null));
  }, []);

  useEffect(() => {
    fim.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [mensagens, enviando]);

  const enviar = useCallback(async (pergunta) => {
    const conteudo = (pergunta ?? texto).trim();
    if (!conteudo || enviando) return;
    setTexto("");
    const anexoDaVez = anexo;
    setAnexo(null);
    setMensagens((atual) => [...atual, { papel: "usuario", texto: conteudo, anexo: anexoDaVez }]);
    setEnviando(true);
    try {
      const { data } = await axios.post(
        `${getApiBase()}/chat/mensagem`,
        { texto: conteudo, conversa_id: conversaId, anexos: anexo ? [anexo.anexo_id] : [] },
        { withCredentials: true },
      );
      setConversaId(data.conversa_id);
      setMensagens((atual) => [
        ...atual,
        { papel: "assistente", texto: data.texto, ferramentas: data.ferramentas },
      ]);
    } catch (erro) {
      const detalhe = erro?.response?.data?.detail;
      const mensagem = detalhe?.message || "Não foi possível falar com o modelo agora.";
      // O erro entra na conversa: sumir com ele deixaria a tela travada sem explicação.
      setMensagens((atual) => [...atual, { papel: "erro", texto: mensagem }]);
      if (detalhe?.code === "sem_provedor") setConexao(null);
    } finally {
      setEnviando(false);
    }
  }, [texto, enviando, conversaId, anexo]);

  const anexarArquivo = async (evento) => {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";  // permite reenviar o mesmo arquivo depois
    if (!arquivo) return;
    setErroAnexo("");
    if (arquivo.size > limiteMb * 1024 * 1024) {
      setErroAnexo(
        limiteMb <= LIMITE_MB_PADRAO
          ? `Arquivo acima de ${limiteMb} MB, o limite do plano FREE. No PRO o limite é 100 MB.`
          : `Arquivo acima de ${limiteMb} MB. Envie só a parte que interessa.`,
      );
      return;
    }
    setAnexando(true);
    const corpo = new FormData();
    corpo.append("arquivo", arquivo);
    try {
      const { data } = await axios.post(`${getApiBase()}/chat/anexo`, corpo, { withCredentials: true });
      setAnexo(data);
    } catch (erro) {
      const detalhe = erro?.response?.data?.detail;
      setErroAnexo(detalhe?.message || "Não foi possível ler este arquivo.");
    } finally {
      setAnexando(false);
    }
  };

  const semProvedor = conexao === null;

  return (
    <Layout>
      <div className={`${LEITURA} flex flex-col min-h-[calc(100vh-8rem)]`}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Assistente
          </h1>
          <p className="text-[#335480] mt-1.5">
            Pergunte sobre seus processos, intimações e prazos — ou tire uma dúvida jurídica.
          </p>
        </div>

        {/* A tela não é bloqueada: quem ainda não configurou consegue ver do que
            se trata antes de decidir ligar uma conta. */}
        {semProvedor && (
          <div
            data-testid="faixa-sem-provedor"
            className="border border-[#003882]/20 bg-[#e5edf5] rounded-lg p-5 mb-6 flex flex-wrap items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3 min-w-0">
              <AlertCircle size={20} strokeWidth={1.5} className="text-[#003882] mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-[#001533]">
                Nenhum modelo de IA configurado. Conecte sua conta para começar a conversar — o
                consumo é cobrado direto no seu provedor.
              </p>
            </div>
            <Link to="/configuracoes?ativar=chat">
              <Button data-testid="configurar-modelo" className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold">
                Configurar modelo
              </Button>
            </Link>
          </div>
        )}

        <div className="flex-1 space-y-5" data-testid="conversa">
          {mensagens.length === 0 && (
            <div className="border border-[#D1DEEE] bg-white rounded-lg p-8 text-center" data-testid="chat-vazio">
              <MessagesSquare size={32} strokeWidth={1.5} className="text-[#6685AD] mx-auto mb-3" aria-hidden="true" />
              <p className="text-[#001533] font-medium">Sobre o que quer falar?</p>
              <p className="text-[#335480] mt-1.5">
                O assistente consulta seu acervo antes de responder.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {SUGESTOES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid="sugestao"
                    onClick={() => enviar(s)}
                    disabled={semProvedor || enviando}
                    className="border border-[#D1DEEE] rounded-lg px-3 py-2 text-[#335480] hover:border-[#003882] hover:text-[#003882] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {mensagens.map((m, i) => (
            <div key={i} data-testid={`mensagem-${m.papel}`} className={m.papel === "usuario" ? "flex justify-end" : ""}>
              <div
                className={
                  m.papel === "usuario"
                    ? "bg-[#003882] text-white rounded-lg px-4 py-3 max-w-[85%] whitespace-pre-wrap"
                    : m.papel === "erro"
                      ? "border border-[#D1DEEE] bg-[#F4F7FB] rounded-lg px-4 py-3 text-[#335480]"
                      : "border border-[#D1DEEE] bg-white rounded-lg px-4 py-3 text-[#001533]"
                }
              >
                {/* Só a resposta do modelo é Markdown; a pergunta e o erro são texto. */}
                {m.papel === "assistente" ? <Markdown texto={m.texto} /> : m.texto}
                {m.anexo && (
                  <p className="text-xs mt-2 flex items-center gap-1.5 opacity-80" data-testid="mensagem-anexo">
                    <FileText size={13} strokeWidth={1.5} aria-hidden="true" />
                    {m.anexo.nome}
                  </p>
                )}
                {m.papel === "assistente" && m.ferramentas?.length > 0 && (
                  <p className="text-[#6685AD] text-sm mt-3 flex items-center gap-1.5" data-testid="ferramentas-usadas">
                    <Wrench size={14} strokeWidth={1.5} aria-hidden="true" />
                    {rotuloDasFerramentas(m.ferramentas)}
                  </p>
                )}
              </div>
            </div>
          ))}

          {/* Enquanto o modelo trabalha. O que ele consultou aparece depois,
              na própria resposta — aqui só se diz que está pensando, porque
              daqui não dá para saber se ele está lendo o acervo ou redigindo. */}
          {enviando && (
            <div className="flex items-center gap-2 text-[#335480]" data-testid="pensando">
              <Orb state="reasoning" size={18} label="O assistente está pensando" />
              Pensando...
            </div>
          )}
          <div ref={fim} />
        </div>

        <form
          onSubmit={(evento) => { evento.preventDefault(); enviar(); }}
          className="sticky bottom-0 bg-[#F4F7FB] pt-4 pb-2 mt-6"
        >
          {anexo && (
            <div className="flex items-center gap-2 mb-2 border border-[#D1DEEE] bg-white rounded-lg px-3 py-2" data-testid="anexo-preso">
              <FileText size={16} strokeWidth={1.5} className="text-[#003882] flex-shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-[#001533] text-sm truncate">{anexo.nome}</p>
                <p className="text-[#6685AD] text-xs">
                  {anexo.tipo} · {formatarTamanho(anexo.tamanho)}
                  {anexo.cortado ? " · lido parcialmente (documento longo)" : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAnexo(null)}
                aria-label="Remover anexo"
                data-testid="anexo-remover"
                className="text-[#6685AD] hover:text-[#003882] flex-shrink-0"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {erroAnexo && (
            <p className="text-[#335480] text-sm mb-2" role="alert" data-testid="anexo-erro">{erroAnexo}</p>
          )}

          <div className="flex items-end gap-2 border border-[#D1DEEE] bg-white rounded-lg p-2">
            <input
              ref={campoArquivo}
              type="file"
              accept={FORMATOS_ACEITOS}
              onChange={anexarArquivo}
              className="hidden"
              data-testid="campo-arquivo"
            />
            <button
              type="button"
              onClick={() => campoArquivo.current?.click()}
              disabled={semProvedor || anexando}
              aria-label="Anexar arquivo"
              title={`Anexar contrato, sentença, planilha... (até ${limiteMb} MB)`}
              data-testid="anexar"
              className="text-[#6685AD] hover:text-[#003882] disabled:opacity-40 disabled:cursor-not-allowed p-2 flex-shrink-0"
            >
              {anexando ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} strokeWidth={1.75} />}
            </button>
            <label htmlFor="pergunta" className="sr-only">Sua pergunta</label>
            <textarea
              id="pergunta"
              data-testid="campo-pergunta"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              onKeyDown={(evento) => {
                // Enter envia, Shift+Enter quebra linha — como todo chat.
                if (evento.key === "Enter" && !evento.shiftKey) {
                  evento.preventDefault();
                  enviar();
                }
              }}
              rows={1}
              disabled={semProvedor}
              placeholder={semProvedor ? "Configure um modelo para começar" : "Pergunte alguma coisa"}
              className="flex-1 resize-none bg-transparent px-2 py-2 text-[#001533] placeholder:text-[#6685AD] focus:outline-none disabled:cursor-not-allowed"
            />
            <Button
              type="submit"
              data-testid="enviar-pergunta"
              disabled={!texto.trim() || enviando || semProvedor}
              aria-label="Enviar pergunta"
              className="bg-[#003882] hover:bg-[#00285e] text-white h-10 w-10 p-0 flex-shrink-0"
            >
              <ArrowUp size={18} strokeWidth={2} aria-hidden="true" />
            </Button>
          </div>
          <p className="text-[#6685AD] text-sm mt-2">
            O assistente pode errar. Confira prazos e andamentos no processo antes de agir.
            {anexo ? " O conteúdo do arquivo vai para o seu provedor de IA e consome tokens da sua conta." : ""}
          </p>
        </form>
      </div>
    </Layout>
  );
}
